/**
 * API utilities for consultant operations
 * Handles all consultant-related Firebase calls
 */
import { callFirebaseFunction } from '../firebaseCalls';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../../firebaseClient';

/**
 * Create a new consultant
 * @param {Object} consultantData - Consultant data object
 * @returns {Promise<any>} Created consultant result
 */
export const createConsultant = async (consultantData: any): Promise<any> => {
  return await callFirebaseFunction('createConsultant', { consultantData });
};

/**
 * Update an existing consultant
 * @param {string} consultantId - Consultant ID
 * @param {Object} consultantData - Updated consultant data
 * @returns {Promise<any>} Update result
 */
export const updateConsultant = async (consultantId: string, consultantData: any): Promise<any> => {
  return await callFirebaseFunction('updateConsultant', { consultantId, consultantData });
};

/**
 * Delete a consultant
 * @param {string} consultantId - Consultant ID to delete
 * @returns {Promise<any>} Delete result
 */
export const deleteConsultant = async (consultantId: string): Promise<any> => {
  return await callFirebaseFunction('deleteConsultant', { consultantId });
};

/**
 * Get consultants for the current user/tenant
 * @param {Object} filters - Optional filters (active, company, country, etc.)
 * @returns {Promise<any>} List of consultants
 */
export const getConsultants = async (filters: Record<string, any> = {}): Promise<any> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, consultants: [], error: 'Authentication required' };
    }

    const tokenResult = await user.getIdTokenResult();
    const tenantId = (tokenResult?.claims as any)?.tenantId as string | undefined;
    const role = (tokenResult?.claims as any)?.role as string | undefined;
    const isGlobalAdmin = role === 'globalAdmin';

    // Most tenants are scoped; Firestore rules typically require tenantId constraint for queries.
    if (!isGlobalAdmin && !tenantId) {
      return { success: false, consultants: [], error: 'Tenant context missing' };
    }

    const consultantsRef = collection(db, 'consultants');
    const q = isGlobalAdmin
      ? query(consultantsRef)
      : query(consultantsRef, where('tenantId', '==', tenantId));

    const snapshot = await getDocs(q);
    let consultants = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    // Avoid composite index needs: apply optional filters client-side.
    if (filters?.active === true) {
      consultants = consultants.filter((c: any) => c?.isActive === true);
    }
    if (typeof filters?.company === 'string' && filters.company.trim()) {
      consultants = consultants.filter((c: any) => c?.company === filters.company);
    }
    if (typeof filters?.country === 'string' && filters.country.trim()) {
      consultants = consultants.filter((c: any) => c?.country === filters.country);
    }

    consultants.sort((a: any, b: any) => {
      const lastA = String(a?.lastName || '').toLowerCase();
      const lastB = String(b?.lastName || '').toLowerCase();
      const lastCmp = lastA.localeCompare(lastB);
      if (lastCmp !== 0) return lastCmp;
      const firstA = String(a?.firstName || '').toLowerCase();
      const firstB = String(b?.firstName || '').toLowerCase();
      return firstA.localeCompare(firstB);
    });

    return { success: true, consultants };
  } catch (error: any) {
    return { success: false, consultants: [], error: error?.message || 'Failed to load consultants' };
  }
};

/**
 * Get a specific consultant by ID
 * @param {string} consultantId - Consultant ID
 * @returns {Promise<any>} Consultant data
 */
export const getConsultant = async (consultantId: string): Promise<any> => {
  return await callFirebaseFunction('getConsultant', { consultantId });
};

/**
 * Search consultants by name, email, or company
 * @param {string} searchQuery - Search query string
 * @param {Object} filters - Optional additional filters
 * @returns {Promise<any>} List of matching consultants
 */
export const searchConsultants = async (searchQuery: string, filters: Record<string, any> = {}): Promise<any> => {
  return await callFirebaseFunction('searchConsultants', { searchQuery, filters });
};

/**
 * Get consultant analytics
 * @param {string} consultantId - Optional specific consultant ID
 * @returns {Promise<any>} Analytics data
 */
export const getConsultantAnalytics = async (consultantId: string | null = null): Promise<any> => {
  return await callFirebaseFunction('getConsultantAnalytics', { consultantId });
};

/**
 * Link a consultant to a registered user
 * @param {string} consultantId - Consultant ID
 * @param {string} userId - User ID to link
 * @returns {Promise<any>} Link result
 */
export const linkConsultantToUser = async (consultantId: string, userId: string): Promise<any> => {
  return await callFirebaseFunction('linkConsultantToUser', { consultantId, userId });
};

/**
 * Get presentations for a specific consultant
 * @param {string} consultantId - Consultant ID
 * @param {Object} filters - Optional filters
 * @returns {Promise<any>} List of presentations
 */
export const getConsultantPresentations = async (consultantId: string, filters: Record<string, any> = {}): Promise<any> => {
  return await callFirebaseFunction('getConsultantPresentations', { consultantId, filters });
};